import type {
  NewChatEvent,
  NewChatTemplateEvent,
  Message,
  UserEntryChatEvent,
  UserTookChatEvent,
  UserExitChatEvent,
  ChatFinishedEvent,
  VisitorTypingEvent,
  TransferRequestEvent,
  TransferAcceptedEvent,
  ChatTransferredToCampaignEvent,
  ChatTransferredToBotEvent,
  ChatUpdatedEvent,
  ChatClosedByTimeoutClientEvent,
  ChatReleasedByUserTimeoutEvent,
  ChatReleasedByAgentLogoutEvent,
  ChatReleasedByBulkTemplateEvent,
  ChatExpiredByMetaWindowEvent,
} from '../types/interfaces'
import { ChatStatus, MessageSourceType } from '../types/enums'
import type { useChatStore } from '../stores/useChatStore'
import type { useNotifications } from '@/composables/useNotifications'
import { getContactIdentifier, toChatIdentity } from './contactIdentity'
import { getMessagePreviewText } from './messageContentFormatter'
import { sortChatMessages } from './messageOrdering'
import { instanceI18n } from '@/plugins/i18n'

export const createChatEventHandlers = (
  chatStore: ReturnType<typeof useChatStore>,
  notifications: ReturnType<typeof useNotifications>,
) => {
  const t = instanceI18n.global.t
  const normalizeSourceType = (sourceType: unknown): MessageSourceType => {
    const normalized = String(sourceType || '').toUpperCase()

    if (normalized === MessageSourceType.ENTRANTE) {
      return MessageSourceType.ENTRANTE
    }

    if (normalized === MessageSourceType.TEMPLATE) {
      return MessageSourceType.TEMPLATE
    }

    return MessageSourceType.SALIENTE
  }

  const normalizeMessages = (messages: Message[]): Message[] =>
    (messages || []).map((message) => ({
      ...message,
      source_type: normalizeSourceType(message.source_type),
      content: message.content,
    }))

  const buildNewChatModel = (
    data: NewChatEvent | NewChatTemplateEvent,
    assignedUserId: string | null,
  ) => {
    const normalizedMessages = normalizeMessages(data.messages)
    const lastMessage =
      normalizedMessages.length > 0 ? normalizedMessages[normalizedMessages.length - 1] : null

    const messageContent = getMessagePreviewText(lastMessage?.content)

    return {
      id: data.chat.id,
      chat_id: data.chat.id,
      ...toChatIdentity(data.chat),
      last_message: messageContent,
      last_message_time: lastMessage?.date || data.chat.start_date,
      // Sin insignia: NEW_CHAT es un chat de la cola, que ven todos los agentes
      // de la campana y no es de nadie todavia. handleNewChatTemplate calcula la
      // suya aparte.
      unread_count: 0,
      status: data.chat.status,
      campaign_id: data.chat.campaign_id || '',
      campaign_name: '',
      incoming_route_id: data.chat.incoming_route_id,
      messages: normalizedMessages,
      user_id: assignedUserId,
      created_at: data.chat.start_date,
    }
  }

  const clearStaleClosures = (chatId: string) => {
    chatStore.setChatTimeoutClosure(chatId, null)
    chatStore.setChatBulkTemplateRelease(chatId, null)
  }

  const handleNewChat = (data: NewChatEvent) => {
    const newChat = buildNewChatModel(data, null)
    chatStore.addChat(newChat)
    clearStaleClosures(data.chat.id)

    notifications.notifyNewChat({
      contactName: newChat.contact_name,
      contactPhone: getContactIdentifier(data.chat.phone, data.chat.username),
      chatId: data.chat.id,
      // NEW_CHAT es la cola de la campana: el agente puede silenciarla.
      isQueued: true,
    })
  }

  // NEW_CHAT_TEMPLATE llega por dos caminos: la plantilla que el propio agente
  // acaba de enviar desde "Iniciar conversacion" (ultimo mensaje: esa plantilla,
  // saliente) o la respuesta del cliente que dejo el chat asignado al agente
  // (ultimo mensaje: entrante). Solo el segundo trae algo por leer.
  const lastMessageIsIncoming = (data: NewChatTemplateEvent) => {
    const messages = data.messages || []
    const lastMessage = messages[messages.length - 1]

    return normalizeSourceType(lastMessage?.source_type) === MessageSourceType.ENTRANTE
  }

  const handleNewChatTemplate = (data: NewChatTemplateEvent) => {
    const fromClientReply = lastMessageIsIncoming(data)
    // addChat reemplaza el chat completo, asi que al reutilizar un chat existente
    // (reactivacion de un EXPIRE, por ejemplo) se conserva lo que ya tenia sin
    // leer en vez de reiniciarlo o inventarle uno nuevo.
    const previousUnread =
      chatStore.chats.find((chat) => chat.chat_id === data.chat.id)?.unread_count ?? 0
    const unreadCount =
      chatStore.selectedChatId === data.chat.id
        ? 0
        : fromClientReply
          ? Math.max(previousUnread, 1)
          : previousUnread

    const newChat = buildNewChatModel(data, chatStore.currentUserId || null)
    chatStore.addChat({ ...newChat, unread_count: unreadCount })
    clearStaleClosures(data.chat.id)

    // La plantilla que envio el propio agente no se anuncia como chat nuevo.
    if (!fromClientReply) return

    notifications.notifyNewChat({
      contactName: newChat.contact_name,
      contactPhone: getContactIdentifier(data.chat.phone, data.chat.username),
      chatId: data.chat.id,
    })
  }

  const handleUserEntryChat = (data: UserEntryChatEvent) => {
    // USER_ENTRY_CHAT es un evento informativo del flujo colaborativo:
    // otro usuario ya se unio a un chat compartido y backend lo notifica
    // a los demas participantes conectados a ese chat.
    void data
  }

  const handleUserTookChat = (data: UserTookChatEvent) => {
    const existingChat = chatStore.chats.find((c) => c.chat_id === data.chat_id)
    if (!existingChat) return

    const isCurrentUserTakingChat = data.user_id === chatStore.currentUserId
    if (!isCurrentUserTakingChat) {
      if (chatStore.selectedChatId === data.chat_id) {
        chatStore.selectChat(null)
      }
      chatStore.removeChat(data.chat_id)
      return
    }

    // El usuario tomó el chat: limpiar el closure de timeout si existía
    chatStore.setChatTimeoutClosure(data.chat_id, null)

    chatStore.updateChatUser(data.chat_id, data.user_id, data.user_id)
    chatStore.updateChat(data.chat_id, {
      campaign_id: data.campaign_id,
      status: data.chat_status as ChatStatus,
    })

    if (data.interaction_message) {
      const chat = chatStore.chats.find((c) => c.chat_id === data.chat_id)
      if (!chat) return

      const normalizedInteractionMessage = normalizeMessages([data.interaction_message])[0]
      const existingMessageIndex = chat.messages.findIndex(
        (message) => message.id === normalizedInteractionMessage.id,
      )

      if (existingMessageIndex !== -1) {
        chat.messages[existingMessageIndex] = normalizedInteractionMessage
      } else {
        chat.messages.push(normalizedInteractionMessage)
      }

      sortChatMessages(chat.messages)
      chat.last_message = getMessagePreviewText(normalizedInteractionMessage.content)
      chat.last_message_time = normalizedInteractionMessage.date
    }
  }

  const handleUserExitChat = (data: UserExitChatEvent) => {
    // USER_EXIT_TO_CHAT es un evento informativo del flujo colaborativo:
    // otro usuario ya salio del chat compartido y backend lo notifica
    // a los demas participantes que siguen conectados.
    void data
  }

  const handleVisitorTyping = (data: VisitorTypingEvent) => {
    chatStore.setChatTyping(data.chat_id)
  }

  const handleChatFinished = (data: ChatFinishedEvent) => {
    chatStore.updateChatStatus(data.chat_id, ChatStatus.FINISH)

    if (chatStore.selectedChatId === data.chat_id) {
      chatStore.selectChat(null)
    }
    chatStore.removeChat(data.chat_id)
  }

  const handleTransferRequest = (data: TransferRequestEvent) => {
    const fromData = data.from || data.from_
    const toData = data.to

    if (!fromData || !toData) {
      return
    }

    const contactName = data.contact_name || t('whatsapp.main.unknown_contact')
    const contactPhone = data.contact_phone || ''

    notifications.notifyTransferRequest({
      fromUserName: fromData.user_name,
      contactName,
      chatId: data.chat_id,
    })

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('transfer-request', {
          detail: {
            chatId: data.chat_id,
            contactName,
            contactPhone,
            transfer_message: data.transfer_message || '',
            fromUserId: fromData.user_id,
            fromUserName: fromData.user_name,
            fromCampaignId: fromData.campaign_id,
            fromCampaignName: fromData.campaign_name,
            toUserId: toData.user_id,
            toUserName: toData.user_name,
            toCampaignId: toData.campaign_id,
            toCampaignName: toData.campaign_name || '',
          },
        }),
      )
    }
  }

  const handleTransferAccepted = (data: TransferAcceptedEvent) => {
    if (!data.is_transfered) return

    const chat = chatStore.chats.find((c) => c.chat_id === data.chat_id)
    if (chat) {
      if (chatStore.selectedChatId === data.chat_id) {
        chatStore.selectChat(null)
      }

      chatStore.removeChat(data.chat_id)
    }

    notifications.playNotificationSound('alert')
  }

  const handleChatTransferredToCampaign = (data: ChatTransferredToCampaignEvent) => {
    const chatId = data.chat.id
    if (!chatId) {
      return
    }

    const identity = toChatIdentity(data.chat)
    const contactName = identity.contact_name || t('whatsapp.main.unknown_contact')
    const campaignId = data.campaign_id || data.chat.campaign_id || ''
    const campaignName = data.campaign_name || ''
    const hasMessages = Array.isArray(data.chat.messages) && data.chat.messages.length > 0
    const normalizedMessages = hasMessages ? normalizeMessages(data.chat.messages!) : null
    const lastMessage = normalizedMessages?.[normalizedMessages.length - 1] || null
    const lastMessagePreview = getMessagePreviewText(lastMessage?.content)
    const lastMessageTime = lastMessage?.date || data.chat.start_date || new Date().toISOString()

    const existingChat = chatStore.chats.find((c) => c.chat_id === chatId)
    const incomingRouteId = data.chat.incoming_route_id || existingChat?.incoming_route_id || ''
    // El backend decide el estado: un chat con la ventana de 24 h cerrada
    // (EXPIRE/TEMPLATE) cambia de campaña sin volver a PENDING.
    const status = data.chat.status || ChatStatus.PENDING

    if (existingChat) {
      chatStore.updateChat(chatId, {
        ...identity,
        contact_name: contactName,
        status,
        campaign_id: campaignId,
        campaign_name: campaignName,
        user_id: null,
        ...(normalizedMessages
          ? {
              messages: normalizedMessages,
              last_message: lastMessagePreview,
              last_message_time: lastMessageTime,
            }
          : {}),
        ...(incomingRouteId ? { incoming_route_id: incomingRouteId } : {}),
      })
    } else {
      chatStore.addChat({
        id: chatId,
        chat_id: chatId,
        ...identity,
        contact_name: contactName,
        last_message: lastMessagePreview || t('whatsapp.chat_events.file'),
        last_message_time: lastMessageTime,
        unread_count: 0,
        status,
        campaign_id: campaignId,
        campaign_name: campaignName,
        incoming_route_id: incomingRouteId,
        messages: normalizedMessages || [],
        user_id: null,
        created_at: data.chat.start_date,
      })
    }

    if (chatStore.selectedChatId === chatId) {
      chatStore.selectChat(null)
    }
  }

  const handleChatTransferredToBot = (data: ChatTransferredToBotEvent) => {
    if (chatStore.selectedChatId === data.chat_id) {
      chatStore.selectChat(null)
    }

    chatStore.removeChat(data.chat_id)
  }

  const handleChatUpdated = (data: ChatUpdatedEvent) => {
    const existingChat = chatStore.chats.find((c) => c.chat_id === data.chat_id)

    if (existingChat) {
      // Actualizar chat existente con la nueva información
      chatStore.updateChat(data.chat_id, {
        ...toChatIdentity(data.chat),
        status: data.chat.status,
        campaign_id: data.chat.campaign_id,
      })
    }
  }

  const handleChatClosedByTimeout = (data: ChatClosedByTimeoutClientEvent) => {
    const normalizedMessages = normalizeMessages(data.chat.messages)
    const lastMessage =
      normalizedMessages.length > 0 ? normalizedMessages[normalizedMessages.length - 1] : null
    const existingChat = chatStore.chats.find((c) => c.chat_id === data.chat_id)

    if (!existingChat) {
      // El chat no existe: insertarlo con ATTENTION para que permanezca en el tab assigned
      chatStore.addChat({
        id: data.chat.id,
        chat_id: data.chat.id,
        ...toChatIdentity(data.chat),
        last_message: getMessagePreviewText(lastMessage?.content),
        last_message_time: lastMessage?.date || data.chat.start_date,
        // El aviso ya lo dan el banner y el sonido: la insignia cuenta
        // mensajes del cliente sin leer, y este chat vuelve sin ninguno nuevo.
        unread_count: 0,
        status: ChatStatus.ATTENTION,
        campaign_id: data.chat.campaign_id || '',
        campaign_name: '',
        incoming_route_id: data.chat.incoming_route_id,
        messages: normalizedMessages,
        user_id: null,
        created_at: data.chat.start_date,
      })
    } else {
      existingChat.messages = normalizedMessages
      if (lastMessage) {
        existingChat.last_message = getMessagePreviewText(lastMessage.content)
        existingChat.last_message_time = lastMessage.date
      }
    }

    chatStore.setChatTimeoutClosure(data.chat_id, {
      campaignId: data.campaign_id,
      botId: data.bot_id,
    })

    // No cambiar el status — el banner se controla por setChatTimeoutClosure,
    // mantener el status actual evita que el chat desaparezca del tab assigned.
    chatStore.updateChat(data.chat_id, { user_id: null })

    notifications.playNotificationSound('alert')
  }

  const handleChatReleasedByUserTimeout = (data: ChatReleasedByUserTimeoutEvent) => {
    const existingChat = chatStore.chats.find((c) => c.chat_id === data.chat_id)

    if (!existingChat) {
      // El chat fue removido del store cuando otro agente lo tomó: reconstruirlo
      const normalizedMessages = normalizeMessages(data.chat.messages)
      const lastMessage =
        normalizedMessages.length > 0 ? normalizedMessages[normalizedMessages.length - 1] : null

      chatStore.addChat({
        id: data.chat.id,
        chat_id: data.chat.id,
        ...toChatIdentity(data.chat),
        last_message: getMessagePreviewText(lastMessage?.content),
        last_message_time: lastMessage?.date || data.chat.start_date,
        // El aviso ya lo dan el banner y el sonido: la insignia cuenta
        // mensajes del cliente sin leer, y este chat vuelve sin ninguno nuevo.
        unread_count: 0,
        status: data.chat.status,
        campaign_id: data.chat.campaign_id || '',
        campaign_name: '',
        incoming_route_id: data.chat.incoming_route_id,
        messages: normalizedMessages,
        user_id: null,
        created_at: data.chat.start_date,
      })
    } else {
      // El chat existe: actualizarlo y reemplazar mensajes
      const normalizedMessages = normalizeMessages(data.chat.messages)
      const lastMessage =
        normalizedMessages.length > 0 ? normalizedMessages[normalizedMessages.length - 1] : null

      existingChat.messages = normalizedMessages
      if (lastMessage) {
        existingChat.last_message = getMessagePreviewText(lastMessage.content)
        existingChat.last_message_time = lastMessage.date
      }

      chatStore.updateChat(data.chat_id, {
        user_id: null,
        status: data.chat.status,
      })
    }

    notifications.playNotificationSound('alert')
  }

  const handleChatReleasedByBulkTemplate = (data: ChatReleasedByBulkTemplateEvent) => {
    const existingChat = chatStore.chats.find((c) => c.chat_id === data.chat_id)

    if (existingChat) {
      if (data.interaction_message) {
        existingChat.messages = [...(existingChat.messages ?? []), data.interaction_message]
        existingChat.last_message = getMessagePreviewText(data.interaction_message.content)
        existingChat.last_message_time = data.interaction_message.date
      }

      chatStore.updateChat(data.chat_id, {
        user_id: null,
        status: data.chat_status as ChatStatus,
      })
    }

    chatStore.setChatBulkTemplateRelease(data.chat_id, data.close_reason)
  }

  const handleChatExpiredByMetaWindow = (data: ChatExpiredByMetaWindowEvent) => {
    const existingChat = chatStore.chats.find((c) => c.chat_id === data.chat_id)
    if (!existingChat) {
      return
    }

    // Sincronizar mensajes desde el snapshot (incluye el mensaje de interacción
    // "La ventana de 24 horas de Meta expiro." guardado por el backend)
    const normalizedMessages = normalizeMessages(data.chat.messages)
    const lastMessage =
      normalizedMessages.length > 0 ? normalizedMessages[normalizedMessages.length - 1] : null

    existingChat.messages = normalizedMessages
    if (lastMessage) {
      existingChat.last_message = getMessagePreviewText(lastMessage.content)
      existingChat.last_message_time = lastMessage.date
    }

    // El backend no libera a los agentes asignados: solo cambia el status
    chatStore.updateChat(data.chat_id, { status: ChatStatus.EXPIRE })

    notifications.notifySystemAction({
      title: t('whatsapp.chat_events.meta_window_expired_title'),
      body: t('whatsapp.chat_events.meta_window_expired_body', {
        contact: toChatIdentity(data.chat).contact_name,
      }),
      chatId: data.chat_id,
    })
  }

  const handleChatReleasedByAgentLogout = (data: ChatReleasedByAgentLogoutEvent) => {
    const alreadyExists = chatStore.chats.some((c) => c.chat_id === data.chat.id)
    if (alreadyExists) return

    const normalizedMessages = normalizeMessages(data.chat.messages)
    const lastMessage =
      normalizedMessages.length > 0 ? normalizedMessages[normalizedMessages.length - 1] : null

    chatStore.addChat({
      id: data.chat.id,
      chat_id: data.chat.id,
      ...toChatIdentity(data.chat),
      last_message: getMessagePreviewText(lastMessage?.content),
      last_message_time: lastMessage?.date || data.chat.start_date,
      // Igual que arriba: el reingreso de un chat liberado no es un mensaje
      // sin leer.
      unread_count: 0,
      status: data.chat.status,
      campaign_id: data.chat.campaign_id || '',
      campaign_name: '',
      incoming_route_id: data.chat.incoming_route_id,
      messages: normalizedMessages,
      user_id: null,

      created_at: data.chat.start_date,
    })

    // El chat vuelve a la cola y el aviso llega a toda la campana: respeta el
    // interruptor de notificaciones de cola.
    notifications.notifyQueuedChatAvailable()
  }

  return {
    handleNewChat,
    handleNewChatTemplate,
    handleUserEntryChat,
    handleUserTookChat,
    handleUserExitChat,
    handleChatFinished,
    handleVisitorTyping,
    handleTransferRequest,
    handleTransferAccepted,
    handleChatTransferredToCampaign,
    handleChatTransferredToBot,
    handleChatUpdated,
    handleChatClosedByTimeout,
    handleChatReleasedByUserTimeout,
    handleChatReleasedByAgentLogout,
    handleChatReleasedByBulkTemplate,
    handleChatExpiredByMetaWindow,
  }
}
